// ── Client names on the dashboard lists (Calem, report 101b5c69, P0) ───────────────────────────────
// "All areas of this dashboard … remove all titles and middle names from client details and do not
// include Co., Limited, or Partnership Limited, etc. Only include this information on quotations,
// invoices, and receipts that are emailed to clients."
// Display-only: a list tidies the name just before it shows it. Nothing is saved or rewritten — forms,
// email composers, the export PDF and the documents a client receives keep the full stored name, and
// every search still matches it. Pickers, dropdowns and the email-thread headers show a company through
// displayCompanyName as well (Calem, report fcc6685f: "Remove any remaining co-limiteds or partnership
// limiteds from company names in the system"); what they pick is still the stored name or id. The one
// place a short name is written: Tracking's Company box puts the tidied name into the item's note.
//   displayClientName   a Client column, chip or tooltip: a company loses its legal form, a person
//                       their titles, and a person whose client file holds First + Last shows only those
//   displayCompanyName  a Company column: the legal form off and nothing else, so a trade name such as
//                       "Dr. Pet Clinic Co., Ltd." keeps its "Dr."
//   displayPersonName   a person's titles off, the rest as stored
//   "Mr. Calem Dann Brooks" (First Calem, Last Brooks on file) → "Calem Brooks"
//   "Ms. Raelene Burgis & Mr. Nicholas Lawrence" → "Raelene Burgis & Nicholas Lawrence"
//   "นางสาวสมหญิง ใจดี" → "สมหญิง ใจดี", but "นายิกา" is a name and stays
//   "Samui Host Co., Ltd." → "Samui Host" · "บริษัท สมุย โฮสต์ จำกัด" → "สมุย โฮสต์" · "Chao Qun Co., Ltd. [K4]" → "Chao Qun [K4]"
// Server mirror (the Calendar's job-reminder titles, and First + Last for the bank list):
// server/src/services/displayName.ts — keep the two in step.

import { honorificRe, nativeTitleRe, legalHeadRe, legalTailRe, nameMarkersRe } from './statutory';

const clean = (v: any): string => (v == null ? '' : String(v)).replace(/\s+/g, ' ').trim();

// A title is the word, then its full stop and/or a space: "Mr. ", "Mr ", or "Mr." run into the name.
// Titles come off — a run of them too, "Prof. Dr." — at the start and after each "&", "and", "," or
// "/", because a joint client names two people. A leading "Mr. & Mrs." (or "Mr and Mrs") goes as one pair.
// The words: English (+ Mx, Prof) and the region country's own (utils/statutory.ts — Thailand: Khun).
const TITLE_OPTS = { extra: ['mx', 'prof'] };
const TITLE_PAIR = () => honorificRe('^(?:{H})\\.?\\s*(?:&|and\\b)\\s*(?=(?:{H})\\b)', 'i', TITLE_OPTS);
const TITLE = () => honorificRe('(^|(?:[&,]|\\/|\\sand\\b)\\s*)(?:(?:{H})(?:\\.\\s*|\\s+))+(?=\\S)', 'gi', TITLE_OPTS);
// Titles in the local script are written straight onto the name, so one comes off only where a name can
// start after it (Thailand: นาย / นาง / นางสาว, then a space, a consonant or a leading vowel เ แ โ ใ ไ).
// "นายิกา" is a name, not นาย + ิกา, and "นางสาวิตรี" falls back from นางสาว to นาง + สาวิตรี.

// A company's legal form: English at the end ("Co., Ltd.", "Company Limited", "Ltd., Part.",
// "Partnership Limited" …), Thai around the name (บริษัท … จำกัด (มหาชน), ห้างหุ้นส่วนจำกัด …, บจก. …).
// Whole words only, and "Co" only as "Co." with its full stop: "Samui Eco Ltd." keeps "Eco", and
// "… Unlimited" keeps its name. "Corp" and "Corporation" are part of the name, not a form, and stay
// (report 63e77e21).
const EN_FORM = /[\s,&]*\b(?:public\s+company\s+limited|(?:co|company)\.?\s*,?\s*(?:ltd|limited)|(?:ltd|limited)\.?\s*,?\s*part(?:nership)?|part(?:nership)?\.?\s*,?\s*(?:ltd|limited)|(?:pte|pty)\.?\s*ltd|sp\.?\s*z\s*o\.?\s*o|partnership|limited|ltd|pcl|plc|inc|llc|llp|gmbh|kft|co(?=\.))\.?\s*$/i;
// The local legal form around the name and its bracketed qualifier are the region country's
// (utils/statutory.ts legalHeadRe / legalTailRe — Thailand: บริษัท … จำกัด (มหาชน), ห้างหุ้นส่วนจำกัด …, บจก. …).
// A client file's own markers after the legal name — "(Building)", "(2025)", "[K4]" — tell
// near-identical files apart and stay; the form in front of them goes. The qualifier belongs to the form.

/** A person's name with every title off; the rest exactly as stored. */
export function displayPersonName(name: any): string {
  const n = clean(name);
  const native = nativeTitleRe();
  const t = n.replace(TITLE_PAIR(), '').replace(TITLE(), '$1');
  const out = (native ? t.replace(native, '') : t).trim();
  return out || n;
}

/** A company's name without its legal form. */
export function displayCompanyName(name: any): string {
  const n = clean(name);
  const m = nameMarkersRe().exec(n);
  const base = m ? n.slice(0, m.index) : n;
  const head = legalHeadRe(), tail = legalTailRe();
  let local = base;
  if (head) local = local.replace(head, '');
  if (tail) local = local.replace(tail, '');
  const bare = local.replace(EN_FORM, '').trim();
  return bare ? bare + (m ? m[0] : '') : n;
}

/**
 * The name a list shows for a client: a name carrying a legal form is a company and loses the form;
 * any other name loses its titles. `first` / `last` are the client file's First and Last. They replace
 * the name only while they are plainly the same person — the name, titles off, starts with First and
 * ends with Last, and nothing between them joins a second person ("&", "and", ",", "/") or adds a note
 * in brackets. A company's parts are its contact person's, and parts can be stale; either way the
 * name as stored, titles off, is what shows.
 */
export function displayClientName(name: any, first?: any, last?: any): string {
  const n = clean(name);
  const company = displayCompanyName(n);
  if (company !== n) return company;
  const shown = displayPersonName(n);
  const f = clean(first), l = clean(last);
  if (f && l) {
    const low = shown.toLowerCase(), fl = f.toLowerCase(), ll = l.toLowerCase();
    if (low.length > fl.length + ll.length && low.startsWith(fl + ' ') && low.endsWith(' ' + ll)) {
      const between = low.slice(fl.length, low.length - ll.length);
      if (!['&', ',', '/', '(', ')'].some(ch => between.includes(ch)) && !/\band\b/.test(between)) return `${f} ${l}`;
    }
  }
  return shown;
}

// ── Job titles (Calem, report 3eaa9cee, P0) ─────────────────────────────────────────────────────────
// A job's stored title carries each service line's quantity in full-width brackets, lines joined by " | "
// (server jobsService.ts unitTitle). Report da741476 (P0) set how they show: "Remove the [ 1 ] from the
// end of all jobs … For one director change and two shareholder changes: Director change (1) shareholder
// change (2) … For one director change ONLY: Director change". When every line's quantity is one the
// marks all come off; when any line has another quantity every line reads "(n)", its ones included, so
// the counts can be told apart. A blank mark holds no quantity and never shows. The server prints titles
// the same way (the job emails, the weekly no-finish-date reminder, the Calendar's job reminders, My
// Tasks) through its mirror in server/src/services/displayName.ts — keep the two in step. Display-only,
// like the names above: jobs.specific_job keeps its marks, because the server builds and compares titles
// with them and filing, Document Tracking's job links and the completion documents read them; the
// Specific Job box in Edit shows them too.
//   "90 Day Report 【1】" → "90 Day Report" · "Change Shareholder 【2】" → "Change Shareholder (2)"
//   "Director Change 【1】 | Shareholder Change 【1】" → "Director Change | Shareholder Change"
//   "Director Change 【1】 | Shareholder Change 【2】" → "Director Change (1) | Shareholder Change (2)"
//   "Director Change 【2】 | Shareholder Change 【3】" → "Director Change (2) | Shareholder Change (3)"
const QTY_MARK = /\s*【\s*([^】]*?)\s*】/g;

/** A job title as the screens show it: no marks when every quantity is one, else "(n)" on every line. */
export function jobTitleLabel(title: any): string {
  const t = clean(title);
  const allOne = Array.from(t.matchAll(QTY_MARK), m => m[1]).every(q => !q || Number(q) === 1);
  return clean(t.replace(QTY_MARK, (_m, q: string) => (allOne || !q ? ' ' : ` (${q}) `)));
}
